import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { useModal } from "hooks/useModal";
import { transactionApi } from "store/apis/transactions";
import { vendorsApi } from "store/apis/vendors";
import { merchantsApi } from "store/apis/merchant";
import { TransactionsWithoutVendorModal } from "./TransactionsWithoutVendorModal";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import { useCallback, useRef, useState } from "react";
import { useToast } from "hooks/useToast";
import { pluralize } from "utils/pluralize";
import { useDispatch } from "react-redux";

const { useGetAllTransactionsDataQuery } = transactionApi;

export const ReviewTransactionsWithoutVendor = ({
  groupId,
  entityId,
  season,
}: {
  groupId: string;
  entityId: string;
  season?: number;
}) => {
  const dispatch = useDispatch();
  const reviewModal = useModal();
  const [pageNum, setPageNum] = useState(1);
  const { successToast } = useToast();
  const updatedTransactionIdsRef = useRef(new Set<string>());

  const startDate = season
    ? dayjs().year(season).startOf("year").format(YYYY_MM_DD)
    : null;
  const endDate = season
    ? dayjs().year(season).endOf("year").format(YYYY_MM_DD)
    : null;

  const { data: transactionData, isSuccess } = useGetAllTransactionsDataQuery(
    {
      groupId,
      entityId,
      hasNoVendors: { value: true },
      startDate: { value: startDate },
      endDate: { value: endDate },
      page_num: pageNum,
      cashFlow: { value: "DEBIT" },
    },
    { skip: !groupId || !entityId }
  );

  const {
    transactions = [],
    total_count,
    per_page,
    total_pages,
  } = transactionData || {};

  const handleModalClose = useCallback(() => {
    const count = updatedTransactionIdsRef.current.size;
    if (count > 0) {
      successToast({
        message: `Vendors assigned to ${pluralize(
          count,
          "transaction",
          "transactions"
        )} successfully`,
      });
      updatedTransactionIdsRef.current.clear();

      // Invalidate cache tags to refresh data
      dispatch(transactionApi.util.invalidateTags(["BOOKS_TRANSACTIONS"]));
      dispatch(vendorsApi.util.invalidateTags(["VENDORS"]));
      dispatch(merchantsApi.util.invalidateTags(["TRANSACTION_MERCHANTS"]));
    }
    reviewModal.close();
  }, [successToast, reviewModal, dispatch]);

  const handleVendorUpdate = useCallback(
    (transactionId: string, isDeleted: boolean) => {
      if (isDeleted) {
        updatedTransactionIdsRef.current.delete(transactionId);
      } else {
        updatedTransactionIdsRef.current.add(transactionId);
      }
    },
    []
  );

  return (
    <>
      <Async.Root
        isEmpty={total_count === 0}
        isLoading={false}
        isSuccess={isSuccess}
      >
        <Async.Empty>{null}</Async.Empty>
        <Async.Success>
          <div>
            {total_count && (
              <Banner
                type="error"
                title={`Vendors missing for ${pluralize(
                  total_count,
                  "transaction",
                  "transactions"
                )} ${season ? ` for tax year ${season}` : ""}`}
                description="Assigning vendors is required to ensure accurate 1099 filings. Unassigned transactions may lead to under-reporting or missed vendors."
                cta={
                  <>
                    <Button size="small" onClick={reviewModal.open}>
                      Review transactions
                    </Button>
                  </>
                }
              />
            )}
            <TransactionsWithoutVendorModal
              isOpen={reviewModal.isOpen}
              close={handleModalClose}
              transactions={transactions}
              pageNum={pageNum}
              perPage={per_page || 20}
              totalCount={total_count || 0}
              totalPages={total_pages || 1}
              onPageNumChange={setPageNum}
              onVendorUpdate={handleVendorUpdate}
              entityId={entityId}
              groupId={groupId}
            />
          </div>
        </Async.Success>
      </Async.Root>
    </>
  );
};
